import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  TextField,
  Stack,
  LinearProgress,
  Chip,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { api } from "../services/api";
import { ClothingItem } from "../types/wardrobe";

// Demo mode helper function
const isDemoMode = (): boolean => {
  return localStorage.getItem("demoMode") === "true";
};

interface ClothingItemUploadProps {
  onUploadComplete: (items: ClothingItem | ClothingItem[]) => void;
  triggerOpen?: boolean;
  hidden?: boolean;
}

interface UploadItem {
  file: File;
  name: string;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
  result?: ClothingItem;
}

export const ClothingItemUpload: React.FC<ClothingItemUploadProps> = ({
  onUploadComplete,
  triggerOpen = false,
  hidden = false,
}) => {
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rateLimitStatus, setRateLimitStatus] = useState<{
    isExempt: boolean;
    limit: number;
    remaining: number;
    resetTime: number | null;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch rate limit status on component mount
  useEffect(() => {
    const fetchRateLimitStatus = async () => {
      try {
        const status = await api.getRateLimitStatus();
        setRateLimitStatus(status);
      } catch (error) {}
    };

    fetchRateLimitStatus();
  }, []);

  // Handle triggerOpen prop
  useEffect(() => {
    if (triggerOpen) {
      setDialogOpen(true);
    }
  }, [triggerOpen]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files).map((file) => ({
        file,
        name: file.name.split(".")[0],
        progress: 0,
        status: "pending" as const,
      }));
      setUploadItems((prev) => [...prev, ...newFiles]);
      setDialogOpen(true);
    }
  };

  const handleNameChange = (index: number, newName: string) => {
    setUploadItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, name: newName } : item))
    );
  };

  const handleRemoveItem = (index: number) => {
    setUploadItems((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (item: UploadItem, index: number) => {
    try {
      setUploadItems((prev) =>
        prev.map((i, idx) =>
          idx === index ? { ...i, status: "uploading" } : i
        )
      );

      const formData = new FormData();
      formData.append("image", item.file);
      formData.append("name", item.name.trim() || item.file.name.split(".")[0]);

      const uploadedItem = await api.uploadClothing(item.file, {
        name: item.name.trim() || item.file.name.split(".")[0],
      });
      setUploadItems((prev) =>
        prev.map((i, idx) =>
          idx === index
            ? {
                ...i,
                status: "success",
                progress: 100,
                result: uploadedItem,
              }
            : i
        )
      );

      return uploadedItem;
    } catch (err) {
      setUploadItems((prev) =>
        prev.map((i, idx) =>
          idx === index
            ? {
                ...i,
                status: "error",
                error:
                  err instanceof Error ? err.message : "Failed to upload item",
              }
            : i
        )
      );
      throw err;
    }
  };

  const handleUpload = async () => {
    if (uploadItems.length === 0) {
      fileInputRef.current?.click();
      return;
    }

    setIsUploading(true);
    const pendingItems = uploadItems.filter(
      (item) => item.status === "pending"
    );

    try {
      const results = await Promise.all(
        pendingItems.map((item, index) => uploadFile(item, index))
      );

      // If all uploads were successful, notify parent and clear the form
      const successfulItems = results.filter(Boolean);
      if (successfulItems.length > 0) {
        onUploadComplete(
          successfulItems.length === 1 ? successfulItems[0] : successfulItems
        );

        // Refresh rate limit status after successful uploads
        try {
          const status = await api.getRateLimitStatus();
          setRateLimitStatus(status);
        } catch (error) {}
      }
    } catch (error) {
    } finally {
      setIsUploading(false);
    }
  };

  const clearSuccessfulUploads = () => {
    setUploadItems((prev) => prev.filter((item) => item.status !== "success"));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    // Clear all items when closing dialog
    setUploadItems([]);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept="image/*"
        multiple
        style={{ display: "none" }}
      />

      {!hidden && (
        <Tooltip
          title={isDemoMode() ? "Disabled in demo mode" : ""}
          arrow
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isDemoMode()}
              startIcon={!isUploading && <CloudUploadIcon />}
              sx={{
                minWidth: 120,
                backgroundColor: "#1A1A1A",
                color: "#FFFCF7",
                "&:hover": {
                  backgroundColor: "#404040",
                },
                "&:disabled": {
                  backgroundColor: "#6B7B8C",
                  color: "#FFFCF7",
                },
              }}
            >
              {isUploading ? <CircularProgress size={20} /> : "Upload Items"}
            </Button>
          </span>
        </Tooltip>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#FFFCF7",
            border: "1px solid #C49A88",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: "#1A1A1A" }}>
          Upload Clothing Items
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* File Selection Button */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Tooltip
                title={isDemoMode() ? "Disabled in demo mode" : ""}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading || isDemoMode()}
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      borderColor: "#C49A88",
                      color: "#1A1A1A",
                      "&:hover": {
                        backgroundColor: "#F4EDE4",
                        borderColor: "#B08570",
                      },
                      "&:disabled": {
                        borderColor: "#6B7B8C",
                        color: "#6B7B8C",
                      },
                    }}
                  >
                    Select Images to Upload
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/* Rate Limit Status */}
            {rateLimitStatus && !rateLimitStatus.isExempt && (
              <Alert
                severity={rateLimitStatus.remaining <= 10 ? "warning" : "info"}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  Uploads remaining this week:{" "}
                  <strong>{rateLimitStatus.remaining}</strong> /{" "}
                  {rateLimitStatus.limit}
                  {rateLimitStatus.resetTime && (
                    <span>
                      {" "}
                      (resets in{" "}
                      {Math.ceil(
                        (rateLimitStatus.resetTime - Date.now()) /
                          (1000 * 60 * 60 * 24)
                      )}{" "}
                      days)
                    </span>
                  )}
                </Typography>
              </Alert>
            )}

            {uploadItems.map((item, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  backgroundColor: "#FFFCF7",
                  border: "1px solid #C49A88",
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box flexGrow={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={item.name}
                        onChange={(e) =>
                          handleNameChange(index, e.target.value)
                        }
                        disabled={
                          item.status === "uploading" ||
                          item.status === "success"
                        }
                        placeholder="Leave blank to use file name"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#C49A88",
                            },
                            "&:hover fieldset": {
                              borderColor: "#B08570",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1A1A1A",
                            },
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="#6B7B8C" noWrap>
                      {item.file.name}
                    </Typography>
                    {item.status === "success" && (
                      <CheckCircleIcon sx={{ color: "#AABBA5" }} />
                    )}
                    {item.status === "error" && (
                      <ErrorIcon sx={{ color: "#FF6B6B" }} />
                    )}
                    {item.status === "pending" && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(index)}
                        sx={{ color: "#6B7B8C" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>
                  {item.status === "uploading" && (
                    <LinearProgress
                      variant="indeterminate"
                      sx={{
                        mt: 1,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#C49A88",
                        },
                        "& .MuiLinearProgress-bar2Indeterminate": {
                          backgroundColor: "#AABBA5",
                        },
                      }}
                    />
                  )}
                  {item.status === "error" && (
                    <Typography
                      color="#FF6B6B"
                      variant="caption"
                      sx={{ mt: 1 }}
                    >
                      {item.error}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}

            <Box sx={{ mt: 2 }}>
              {uploadItems.length > 0 && (
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`Total: ${uploadItems.length}`}
                    variant="outlined"
                    sx={{
                      borderColor: "#C49A88",
                      color: "#6B7B8C",
                    }}
                  />
                  <Chip
                    label={`Pending: ${
                      uploadItems.filter((i) => i.status === "pending").length
                    }`}
                    color="primary"
                    variant="outlined"
                    sx={{
                      borderColor: "#1A1A1A",
                      color: "#1A1A1A",
                    }}
                  />
                  <Chip
                    label={`Completed: ${
                      uploadItems.filter((i) => i.status === "success").length
                    }`}
                    sx={{
                      backgroundColor: "#AABBA5",
                      color: "#1A1A1A",
                    }}
                  />
                  {uploadItems.some((i) => i.status === "error") && (
                    <Chip
                      label={`Failed: ${
                        uploadItems.filter((i) => i.status === "error").length
                      }`}
                      sx={{
                        backgroundColor: "#FF6B6B",
                        color: "#FFFCF7",
                      }}
                    />
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: "#6B7B8C" }}>
            Close
          </Button>
          {uploadItems.some((item) => item.status === "success") && (
            <Button
              variant="outlined"
              onClick={clearSuccessfulUploads}
              sx={{
                borderColor: "#C49A88",
                color: "#1A1A1A",
                "&:hover": {
                  backgroundColor: "#F4EDE4",
                  borderColor: "#B08570",
                },
              }}
            >
              Clear Completed
            </Button>
          )}
          <Tooltip
            title={isDemoMode() ? "Disabled in demo mode" : ""}
            arrow
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={
                  isUploading ||
                  uploadItems.length === 0 ||
                  isDemoMode() ||
                  (rateLimitStatus
                    ? !rateLimitStatus.isExempt &&
                      rateLimitStatus.remaining <= 0
                    : false)
                }
                startIcon={!isUploading && <CloudUploadIcon />}
                sx={{
                  backgroundColor: "#1A1A1A",
                  color: "#FFFCF7",
                  "&:hover": {
                    backgroundColor: "#404040",
                  },
                  "&:disabled": {
                    backgroundColor: "#6B7B8C",
                    color: "#FFFCF7",
                  },
                }}
              >
                {isUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  `Upload ${
                    uploadItems.filter((i) => i.status === "pending").length
                  } Items`
                )}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
};
